/**
 * The explorations overview: every exploration as a card, filtered by laser
 * device, substrate and project (client-side on the full list) and by the
 * Mine/All toggle (server-side). The card is ExplorationCard, shared with
 * the Projects page; the folder button in its header files it in a project.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { EXPLORATION_SETUP_ROUTE, explorationRoute, explorationResumeRoute } from "../../helper/explorationRoutes";
import { deleteRights, useDeleteRights } from "../../helper/deleteRights";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, MenuItem } from "@mui/material";
import { Alert } from "@mui/material";
import ResourcePanel, { PanelButton, PanelClearFilters, PanelScopeRow } from "../../components/ResourcePanel";
import ResourcePageLayout from "../../components/ResourcePageLayout";
import { ITEM_KIND_STYLES } from "../../components/itemKindStyles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { LIST_SCOPE_MINE, useListScope, withListScope } from "../../components/ListScopeToggle";
import ExplorationCard, { normalizeExplorationStatus } from "../../components/ExplorationCard";
import ProjectFilterSelect, { ALL_PROJECTS, matchesProjectFilter } from "../../components/ProjectFilterSelect";
import useProjects from "../../components/useProjects";
import { moveItems, projectErrorMessage } from "../../helper/projects";

/** The explorations' tones: the panel band and its New button share them. */
const KIND = ITEM_KIND_STYLES.exploration;
const KIND_BUTTON_SX = { bgcolor: KIND.header, "&:hover": { bgcolor: KIND.header, filter: "brightness(0.9)" } };

/** Large enough to load all explorations in one request (backend paginates with page=0). */
const EXPLORATION_LIST_PAGE_SIZE = 10000;

/** Sentinel for the "no filter" entry of the laser/substrate dropdowns. */
const ALL = "";

/**
 * Distinct, alphabetically sorted values of one field across the loaded rows.
 * The dropdowns are built from the data itself rather than from the full
 * device/material tables, so they only ever offer values that actually match
 * something.
 */
function distinctValues(rows, field) {
  return Array.from(new Set(rows.map((row) => row[field]).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
}

function ColorExplorationHome() {
  const navigate = useNavigate();
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteDateInput, setDeleteDateInput] = useState("");
  const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
  // "" means "no filter". All filters are combined with AND.
  const [laserFilter, setLaserFilter] = useState(ALL);
  const [materialFilter, setMaterialFilter] = useState(ALL);
  const [projectFilter, setProjectFilter] = useState(ALL_PROJECTS);
  // Mine/All toggle (shared across the overview pages, see ListScopeToggle).
  const [scope, setScope] = useListScope();
  // Whether the signed-in account may delete a row: its creator or an
  // administrator (who is warned in the dialog). Same rule on the server.
  const me = useDeleteRights();
  const rightsOf = (row) => deleteRights(row, me, "exploration");
  const currentRights = currentRow ? rightsOf(currentRow) : null;
  // The team's projects: the filter and the cards' Move-to menu.
  const { projects, reload: reloadProjects } = useProjects();

  // An exploration's pages carry its id in the URL.
  const onShowResultsClick = (row) => {
    navigate(explorationRoute(row.id, "analyse"));
  };

  const onShowButtonClick = (row) => {
    navigate(explorationResumeRoute(row.id, normalizeExplorationStatus(row.status), Boolean(row.results_ready)));
  };

  const handleOpenDeleteDialog = (row) => {
    setCurrentRow(row);
    setDeleteDateInput("");
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setDeleteDateInput("");
  };

  // The date the user must type to arm the delete button (yyyy-mm-dd of the
  // exploration's creation) — a second, deliberate hurdle against deleting
  // the wrong exploration.
  const deleteDateExpected = (() => {
    if (!currentRow?.created_at) return "";
    const parsed = new Date(currentRow.created_at);
    if (Number.isNaN(parsed.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  })();
  const deleteArmed =
    deleteDateExpected.length > 0 && deleteDateInput.trim() === deleteDateExpected;

  const handleConfirmDelete = async () => {
    if (currentRow && deleteArmed) {
      try {
        await axios.delete(`api/exploration/delete/${currentRow.id}/`);
        setDeleteErrorMessage(null);
      } catch (error) {
        // 409: the exploration feeds a selection and stays.
        console.error("Error deleting the exploration:", error);
        setDeleteErrorMessage(
          error?.response?.data?.message || "Deleting the exploration failed.",
        );
      }
      handleCloseDeleteDialog();
      fetchExplorationData();
    }
  };

  const fetchExplorationData = async () => {
    setPageState((old) => ({
      ...old,
      isLoading: true,
    }));
    try {
      const response = await axios.get(
        withListScope(
          `api/exploration/data?page=0&per_page=${EXPLORATION_LIST_PAGE_SIZE}&sort_by=date&sort_order=desc`,
          scope
        )
      );
      if (response.status === 200) {
        setPageState((old) => ({
          ...old,
          data: response.data.data,
        }));
      }
    } catch (error) {
      console.error("Failed to load explorations:", error);
      setPageState((old) => ({
        ...old,
        data: [],
      }));
    } finally {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
    }
  };

  // Re-fetch when the Mine/All toggle changes: the owner filter is applied
  // by the backend (?scope=), unlike the laser/substrate filters below.
  useEffect(() => {
    fetchExplorationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  // Files the exploration in a project (null = unfiled): the card shows the
  // new project right away, the project counts are refreshed.
  const handleMoveToProject = async (row, projectId) => {
    try {
      await moveItems([{ kind: "exploration", id: row.id }], projectId);
      const name = projectId ? projects.find((p) => p.id === projectId)?.name || null : null;
      setPageState((old) => ({
        ...old,
        data: old.data.map((r) => (r.id === row.id ? { ...r, project: projectId, project_name: name } : r)),
      }));
      setDeleteErrorMessage(null);
      reloadProjects();
    } catch (error) {
      console.error("Moving the exploration failed:", error);
      setDeleteErrorMessage(projectErrorMessage(error, "Moving the exploration failed."));
    }
  };

  const laserOptions = useMemo(
    () => distinctValues(pageState.data, "laser_device_name"),
    [pageState.data]
  );
  const materialOptions = useMemo(
    () => distinctValues(pageState.data, "material_name"),
    [pageState.data]
  );
  // Deleting the last exploration of a device/substrate drops it from the
  // options; drop the now-dangling filter with it, or the dropdown would
  // render blank and the grid would stay empty with no way back.
  useEffect(() => {
    if (laserFilter !== ALL && !laserOptions.includes(laserFilter)) setLaserFilter(ALL);
    if (materialFilter !== ALL && !materialOptions.includes(materialFilter))
      setMaterialFilter(ALL);
  }, [laserOptions, materialOptions, laserFilter, materialFilter]);
  const filteredData = pageState.data.filter(
    (row) =>
      (laserFilter === ALL || row.laser_device_name === laserFilter) &&
      (materialFilter === ALL || row.material_name === materialFilter) &&
      matchesProjectFilter(row, projectFilter)
  );
  const filtersActive =
    laserFilter !== ALL || materialFilter !== ALL || projectFilter !== ALL_PROJECTS;

  const clearFilters = () => {
    setLaserFilter(ALL);
    setMaterialFilter(ALL);
    setProjectFilter(ALL_PROJECTS);
  };
  const total = pageState.data.length;
  const panel = (
    <ResourcePanel
      bandSx={{ background: KIND.header }}
      overline={scope === LIST_SCOPE_MINE ? "Your explorations" : "All explorations"}
      // One count, in the panel only: what the list shows, "of" the total
      // while a filter narrows it.
      headline={
        pageState.isLoading
          ? "Loading"
          : filtersActive
            ? `${filteredData.length} of ${total} explorations`
            : `${total} exploration${total === 1 ? "" : "s"}`
      }
      subline={scope === LIST_SCOPE_MINE ? "Created by you" : "All accounts"}
      about="An exploration marks patches of laser parameters on a substrate, measures their colours and breeds the next generation towards its objective. Its measured samples feed the selections."
      filters={
        <>
          <PanelScopeRow value={scope} onChange={setScope} />
          <TextField
            select
            fullWidth
            size="small"
            label="Laser device"
            value={laserFilter}
            onChange={(event) => setLaserFilter(event.target.value)}
            // ALL is the empty string, so the label has to be pinned shrunk
            // and the empty value rendered explicitly.
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value={ALL}>
              <em>All laser devices</em>
            </MenuItem>
            {laserOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Substrate"
            value={materialFilter}
            onChange={(event) => setMaterialFilter(event.target.value)}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value={ALL}>
              <em>All substrates</em>
            </MenuItem>
            {materialOptions.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <ProjectFilterSelect projects={projects} value={projectFilter} onChange={setProjectFilter} sx={{ minWidth: 0, width: "100%" }} />
          {filtersActive ? <PanelClearFilters onClick={clearFilters} /> : null}
        </>
      }
      actions={
        <>
          <PanelButton primary sx={KIND_BUTTON_SX} startIcon={<AddCircleOutlineIcon />} onClick={() => navigate(EXPLORATION_SETUP_ROUTE)}>
            New exploration
          </PanelButton>
          <PanelButton
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchExplorationData();
              reloadProjects();
            }}
            disabled={pageState.isLoading}
          >
            Refresh
          </PanelButton>
        </>
      }
    />
  );

  return (
    <>
      <ResourcePageLayout
        title="Explorations"
        loading={pageState.isLoading}
        loadingLabel="Loading explorations…"
        error={
          deleteErrorMessage ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }} onClose={() => setDeleteErrorMessage(null)}>
              {deleteErrorMessage}
            </Alert>
          ) : null
        }
        count={filteredData.length}
        emptyText={
          filtersActive
            ? "No explorations match the selected filters."
            : scope === LIST_SCOPE_MINE
              ? "You have not started any explorations yet."
              : "No explorations found for this view."
        }
        panel={panel}
        grid
      >
        {filteredData.map((row) => (
          <ExplorationCard
            key={row.id}
            row={row}
            rights={rightsOf(row)}
            onPrimary={() => onShowButtonClick(row)}
            onAnalyse={() => onShowResultsClick(row)}
            onDelete={() => handleOpenDeleteDialog(row)}
            projects={projects}
            onMoveToProject={(projectId) => handleMoveToProject(row, projectId)}
          />
        ))}
      </ResourcePageLayout>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Delete exploration?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This permanently deletes the exploration “{currentRow?.name}” created on{" "}
            <strong>{deleteDateExpected || "—"}</strong> with all of its iterations and
            measurements. This action cannot be undone.
          </DialogContentText>
          {currentRights?.warning && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {currentRights.warning}
            </Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            size="small"
            sx={{ mt: 2 }}
            label="Type the creation date to confirm"
            placeholder={deleteDateExpected}
            value={deleteDateInput}
            onChange={(event) => setDeleteDateInput(event.target.value)}
            error={deleteDateInput.length > 0 && !deleteArmed}
            helperText={
              deleteDateInput.length > 0 && !deleteArmed
                ? `Does not match ${deleteDateExpected}`
                : `Enter ${deleteDateExpected} exactly to enable deletion`
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={!deleteArmed}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ColorExplorationHome;
